// =============================================================================
// SIREPPAR - Panel flotante anclado a un elemento, renderizado en <body>
// =============================================================================
// Resuelve de una vez el problema que se repite en toda la app: un desplegable
// posicionado con `absolute` queda RECORTADO por el primer ancestro con
// `overflow-hidden` — las tarjetas de sección de /documentos lo tienen, así que
// el menú de estado del último archivo de una sección se cortaba contra el borde
// de la tarjeta.
//
// La solución es sacar el panel del árbol y ponerlo en <body> con `fixed`.
// Eso trae tres consecuencias que este componente ya cubre:
//
//   1. El click en el panel es "fuera" del ancla: el cierre por click-outside
//      tiene que consultar el panel además del ancla.
//   2. `fixed` se calcula contra el viewport, así que al scrollear hay que
//      reposicionar. El listener va en fase de CAPTURA porque el scroll que
//      importa suele ser el de un contenedor interno, y ese no burbujea a window.
//   3. Dentro de un <Dialog> de Headless UI el panel queda fuera del
//      Dialog.Panel y el click se lee como "cerrar el modal". Por eso el nodo
//      lleva `data-portal-dropdown`, que Modal consulta para ignorar ese cierre.
//
// Si no entra para abajo pero sí para arriba, se abre hacia arriba.
// =============================================================================

import { ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  /** Elemento contra el que se posiciona el panel. */
  anchorRef: RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  /** Borde del ancla con el que se alinea el panel. */
  align?: 'left' | 'right';
  /** Alto máximo del panel; también decide si abre hacia arriba o hacia abajo. */
  maxHeight?: number;
  /** Ancho mínimo. Por defecto, el ancho del ancla. */
  minWidth?: number;
  className?: string;
  children: ReactNode;
}

/** Aire que se le deja al borde de la ventana. */
const MARGEN_VENTANA = 12;
/** Por debajo de esto el panel no se lee; se prefiere invadir el margen. */
const ALTO_MINIMO = 140;

export default function PopoverPortal({
  anchorRef,
  open,
  onClose,
  align = 'left',
  maxHeight = 320,
  minWidth,
  className = '',
  children,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; alto: number; haciaArriba: boolean } | null>(null);

  const recalcular = () => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    // El alto se RECORTA al espacio que realmente hay, menos un margen: con el alto
    // fijo, un ancla cerca del borde abría el panel hacia arriba igual y la lista se
    // iba de la pantalla, sin forma de scrollear hasta el final. `maxHeight` pasa a
    // ser un techo, no una medida.
    const espacioAbajo = window.innerHeight - rect.bottom - MARGEN_VENTANA;
    const espacioArriba = rect.top - MARGEN_VENTANA;
    const haciaArriba = espacioAbajo < maxHeight && espacioArriba > espacioAbajo;
    const disponible = haciaArriba ? espacioArriba : espacioAbajo;
    // Piso mínimo: con menos que esto el desplegable no sirve para nada, así que se
    // deja tapar un poco el margen antes que mostrar una rendija.
    const alto = Math.max(ALTO_MINIMO, Math.min(maxHeight, disponible));
    const width = Math.max(rect.width, minWidth ?? rect.width);
    // Alineado a un borde del ancla, pero sin salirse de la ventana por ninguno
    // de los dos lados (era el otro síntoma: el panel se iba de la pantalla).
    const deseado = align === 'right' ? rect.right - width : rect.left;
    const left = Math.max(8, Math.min(deseado, window.innerWidth - width - 8));
    setPos({ top: haciaArriba ? rect.top : rect.bottom + 4, left, width, alto, haciaArriba });
  };

  useLayoutEffect(() => {
    if (open) recalcular();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      onClose();
    }
    function handleScrollOrResize() { recalcular(); }

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={panelRef}
      data-portal-dropdown
      className={`fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-2xl flex flex-col overflow-hidden ${className}`}
      style={{
        top: pos.haciaArriba ? undefined : pos.top,
        bottom: pos.haciaArriba ? window.innerHeight - pos.top + 4 : undefined,
        left: pos.left,
        width: pos.width,
        maxHeight: pos.alto,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
